import { inject, Injectable } from '@angular/core';
import { ChatStoreService } from '../chat-store/chat-store.service';
import { combineLatest, map, Observable, of, shareReplay, withLatestFrom } from 'rxjs';
import { Room as ApiRoom } from '../../../shared-api/room';
import { SyncService } from '../sync/sync.service';
import { RoomInfo } from '../../model/room-info';
import { RoomMember as ModelRoomMember } from '../../model/room-member';
import { Room as ModelRoom } from '../../model/room';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
/**
 * Zugriff auf Räume für Anzeige des aktuellen Zustands links
 * (Name, letzte Nachricht, Reihenfolge) sowie Eigenschaften (rechts).
 * Begründung: Keine AFO (technische Hilfsklasse)
 */
export class RoomListAccessService {
  private readonly syncService = inject(SyncService);
  private readonly chatStoreService = inject(ChatStoreService);

  getRoomInfos$(): Observable<RoomInfo[]> {
    return this.syncService.roomInfos$;
  }

  getModelRooms$(): Observable<ModelRoom[]> {
    return this.getRoomInfos$().pipe(
      map(roomInfos => roomInfos.map(roomInfo => roomInfo.modelRoom)),
      shareReplay(1)
    );
  }

  roomList = toSignal(this.getModelRooms$(), { initialValue: [] });

  getRoomInfosSync(): RoomInfo[] {
    let roomInfosSync: RoomInfo[] = [];
    of(true)
      .pipe(withLatestFrom(this.syncService.roomInfos$))
      .subscribe(([, roomInfos]) => (roomInfosSync = roomInfos));

    return roomInfosSync;
  }

  getRoomInfo(roomId: string | undefined): RoomInfo | undefined {
    if (!roomId) {
      return undefined;
    }

    return this.getRoomInfosSync().find(roomInfo => roomInfo.roomId === roomId);
  }

  getModelRoom(roomId: string | undefined): ModelRoom | undefined {
    if (!roomId) {
      return undefined;
    }

    const roomInfos = this.getRoomInfosSync();
    const roomInfo = roomInfos.find(roomInfo => roomInfo.roomId === roomId);

    return roomInfo?.modelRoom;
  }

  getCurrentModelRoom(): ModelRoom | undefined {
    return this.getModelRoom(this.chatStoreService.getStateSync().currentRoomId);
  }

  getModelRoomMember(roomId: string | undefined, userId: string | undefined): ModelRoomMember | undefined {
    if (!userId) {
      return undefined;
    }

    return this.getModelRoom(roomId)?.members.find(member => member.mxid === userId);
  }

  getCurrentRoomInfo$(): Observable<RoomInfo | undefined> {
    return combineLatest([this.syncService.roomInfos$, this.chatStoreService.getCurrentRoomId$()]).pipe(
      map(([roomInfos, currRoomId]) => {
        if (!currRoomId) {
          return undefined;
        }

        return roomInfos.find(ri => ri.roomId === currRoomId);
      })
    );
  }

  getCurrentModelRoom$(): Observable<ModelRoom | undefined> {
    return this.getCurrentRoomInfo$().pipe(
      map(roomInfo => roomInfo?.modelRoom),
      shareReplay(0)
    );
  }

  getCurrentApiRoom$(): Observable<ApiRoom | undefined> {
    return this.getCurrentRoomInfo$().pipe(
      map(roomInfo => roomInfo?.apiRoom),
      shareReplay(0)
    );
  }
}
